import React, { useState, useMemo, useCallback } from 'react';
import type { EpProject, EpMissionLogEntry } from '@types';
import { getFirestore, doc, updateDoc, arrayUnion } from 'firebase/firestore';
import UnifiedMap from './UnifiedMap';
import useGoogleMaps from './useGoogleMaps';
import { ICONS } from './constants';
import { createSafeMarkdown } from '../utils/safeMarkdown';

const md = createSafeMarkdown();
const db = getFirestore();

interface EpOperationsDashboardProps {
    project: EpProject;
    onNavigateToGsoc: () => void;
    onNavigateToVcmc: () => void;
    onBack: () => void;
    onApprove: () => Promise<void>;
}

// ── Helpers ───────────────────────────────────────────────────────────────────
const EMERGENCY_NUMBERS: Record<string, { police: string; ambulance: string; fire: string }> = {
    'united states': { police: '911', ambulance: '911', fire: '911' },
    'united kingdom': { police: '999', ambulance: '999', fire: '999' },
    'australia': { police: '000', ambulance: '000', fire: '000' },
    'canada': { police: '911', ambulance: '911', fire: '911' },
    'nigeria': { police: '112', ambulance: '112', fire: '112' },
    'kenya': { police: '999', ambulance: '999', fire: '999' },
    'south africa': { police: '10111', ambulance: '10177', fire: '10177' },
    'france': { police: '17', ambulance: '15', fire: '18' },
    'germany': { police: '110', ambulance: '112', fire: '112' },
    'default': { police: '112', ambulance: '112', fire: '112' },
};

function getEmergencyNumbers(country?: string) {
    if (!country) return EMERGENCY_NUMBERS['default'];
    const key = country.toLowerCase().trim();
    return EMERGENCY_NUMBERS[key] || EMERGENCY_NUMBERS['default'];
}

// LogEntry type is now EpMissionLogEntry from @types

const EpOperationsDashboard: React.FC<EpOperationsDashboardProps> = ({
    project, onNavigateToGsoc, onNavigateToVcmc, onBack, onApprove
}) => {
    const [activeTab, setActiveTab] = useState<'plan' | 'comms' | 'log'>('plan');
    const [isApproving, setIsApproving] = useState(false);
    // Seed from Firestore-persisted log; fall back to a system init entry
    const [logEntries, setLogEntries] = useState<EpMissionLogEntry[]>(() => {
        const persisted = project.missionLog || [];
        if (persisted.length === 0) {
            return [{ time: new Date().toLocaleTimeString(), author: 'System', message: `Mission Command Center initialised — ${project.name}`, type: 'info' }];
        }
        return persisted;
    });
    const [newLogEntry, setNewLogEntry] = useState('');
    const [logAuthor, setLogAuthor] = useState('Detail Leader');
    const [isSavingLog, setIsSavingLog] = useState(false);
    const { isLoaded } = useGoogleMaps();

    // Threat level badge (Gap 3) — sourced from plannerData or top-level field
    const threatLevel = project.threatLevel || project.plannerData?.ep_pp_threat_level;
    const threatBadgeStyle = (level?: string) => {
        const l = level?.toLowerCase();
        if (l === 'critical') return 'bg-red-600 text-white border-red-700';
        if (l === 'high') return 'bg-orange-500 text-white border-orange-600';
        if (l === 'medium') return 'bg-yellow-400 text-slate-900 border-yellow-500';
        if (l === 'low') return 'bg-emerald-500 text-white border-emerald-600';
        return 'bg-slate-200 text-slate-600 border-slate-300';
    };

    const handleApprove = async () => {
        setIsApproving(true);
        try { await onApprove(); } finally { setIsApproving(false); }
    };

    const handleAddLog = useCallback(async () => {
        if (!newLogEntry.trim()) return;
        const entry: EpMissionLogEntry = {
            time: new Date().toLocaleTimeString(),
            author: logAuthor || 'Unknown',
            message: newLogEntry.trim(),
            type: 'action',
        };
        setLogEntries(prev => [...prev, entry]);
        setNewLogEntry('');
        // Gap 1 — persist to Firestore
        try {
            setIsSavingLog(true);
            const projectRef = doc(db, 'ep_projects', project.id);
            await updateDoc(projectRef, { missionLog: arrayUnion(entry) });
        } catch (e) {
            console.warn('Mission log persist failed:', e);
        } finally {
            setIsSavingLog(false);
        }
    }, [newLogEntry, logAuthor, project.id]);

    // ── Comms plan derived from project data ───────────────────────────────────
    const country = project.plannerData?.ep_pp_location_country || project.plannerData?.country;
    const emergency = getEmergencyNumbers(country);
    const commsData = useMemo(() => ({
        command: project.plannerData?.ep_pp_comms_command || 'Channel 1 (Encrypted)',
        tactical: project.plannerData?.ep_pp_comms_tactical || 'Channel 2 (Tactical)',
        medical: project.plannerData?.ep_pp_comms_medical || 'Channel 9 (Medical / Emergency)',
        duress: project.plannerData?.ep_pp_comms_duress || 'Not configured — set in Advance Survey',
        lostComms: project.plannerData?.ep_pp_comms_lost_comms || 'Rally at last known safe room. Await command via SMS backup.',
        police: emergency.police,
        ambulance: emergency.ambulance,
        fire: emergency.fire,
        country: country || 'Unknown',
    }), [project.plannerData, emergency]);

    // ── Map markers ───────────────────────────────────────────────────────────
    const mapCenter = project.location
        ? { latitude: project.location.lat, longitude: project.location.lng, zoom: 12 }
        : { latitude: 38.9072, longitude: -77.0369, zoom: 10 };

    const mapMarkers = useMemo(() => {
        const markers: any[] = [];
        // Principal location
        if (project.location) {
            markers.push({ id: 'principal', lat: project.location.lat, lng: project.location.lng, title: '⭐ Principal Location', isPrimary: true, type: 'Operational HQ' });
        }
        // Extract route stops from plannerData if present
        const routeStops: string[] = [
            project.plannerData?.ep_pp_location_address,
            project.plannerData?.ep_rp_origin,
            project.plannerData?.ep_rp_destination,
        ].filter(Boolean);
        routeStops.forEach((addr, i) => {
            markers.push({ id: `stop-${i}`, title: addr, type: 'RouteStop' });
        });
        // Nearest hospital from advance survey if stored
        const hosp = project.plannerData?.ep_adv_nearest_hospital;
        if (hosp) markers.push({ id: 'hospital', title: hosp, type: 'Medical' });
        // Safe room
        const safeRoom = project.plannerData?.ep_adv_safe_room;
        if (safeRoom) markers.push({ id: 'saferoom', title: `Safe Room: ${safeRoom}`, type: 'Evac Point' });
        return markers;
    }, [project.location, project.plannerData]);

    // ── Tactical routes (primary / secondary / EER) ───────────────────────────
    const tacticalRoutes = useMemo(() => {
        const routes: any[] = [];
        const origin = project.plannerData?.ep_rp_origin;
        const dest = project.plannerData?.ep_rp_destination;
        if (origin && dest && project.location) {
            routes.push({ name: 'Primary Route', origin, destination: dest, waypoints: [], color: '#3b82f6' });
            routes.push({ name: 'Secondary Route', origin, destination: dest, waypoints: [], color: '#f59e0b', isDash: true });
            routes.push({ name: 'EER', origin, destination: dest, waypoints: [], color: '#ef4444', isDash: true });
        }
        return routes;
    }, [project.plannerData, project.location]);

    return (
        <div className="animate-fade-in h-[calc(100vh-140px)] flex flex-col">
            {/* Header */}
            <div className="mb-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="bg-blue-600 p-2 rounded shadow-lg shadow-blue-200">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                        </svg>
                    </div>
                    <div>
                        <h2 className="text-xs font-bold text-blue-600 uppercase tracking-widest">Enterprise Control Center</h2>
                        <h1 className="text-xl font-black text-slate-800 tracking-tight">MISSION COMMAND</h1>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    {/* Gap 3 — Threat level badge from persisted EP Threat Analysis */}
                    {threatLevel && (
                        <div className="text-center">
                            <div className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-0.5">Threat Posture</div>
                            <span className={`px-3 py-1 text-xs font-black uppercase tracking-widest rounded-full border ${threatBadgeStyle(threatLevel)}`}>
                                {threatLevel}
                            </span>
                        </div>
                    )}
                    <div className="text-right">
                        <div className="text-[10px] font-mono text-slate-400">SYSTEM STATUS: OPTIMAL</div>
                        <div className="text-[10px] font-mono text-emerald-500 flex items-center justify-end gap-1">
                            <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" />
                            LIVE SECURE CONNECTION
                        </div>
                    </div>
                </div>
            </div>

            {/* Approval Banner */}
            {project.status === 'Pending' && (
                <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-center justify-between shadow-sm">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-amber-100 text-amber-600 rounded-full">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                            </svg>
                        </div>
                        <div>
                            <h3 className="font-bold text-amber-900 text-sm">Mission Awaiting Approval</h3>
                            <p className="text-amber-700 text-xs">This operational plan must be reviewed and activated before tracking begins.</p>
                        </div>
                    </div>
                    <button onClick={handleApprove} disabled={isApproving}
                        className="px-6 py-2 bg-amber-600 hover:bg-amber-700 text-white text-sm font-bold rounded-lg shadow-md transition-all active:scale-95 disabled:opacity-50">
                        {isApproving ? 'Activating...' : 'APPROVE & ACTIVATE'}
                    </button>
                </div>
            )}

            {/* Info Bar */}
            <div className="flex justify-between items-center mb-4 bg-white p-4 rounded-lg border border-slate-200 shadow-sm">
                <div className="flex items-center gap-4">
                    <button onClick={onBack} className="text-slate-400 hover:text-slate-600">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
                    </button>
                    <div>
                        <h1 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                            {project.name}
                            <span className={`px-2 py-0.5 text-[10px] font-black rounded-full uppercase tracking-tighter ${project.status === 'Active' ? 'bg-emerald-100 text-emerald-700 border border-emerald-200' : 'bg-amber-100 text-amber-700 border border-amber-200'}`}>
                                {project.status}
                            </span>
                        </h1>
                        <p className="text-xs text-slate-500">
                            Principal: <span className="font-bold text-slate-700">{project.plannerData?.ep_pp_principal_name || 'Not Specified'}</span>
                            {' · '}AO: <span className="font-bold text-slate-700">{country || 'Unknown'}</span>
                            {' · '}ID: <span className="font-mono">{project.id}</span>
                        </p>
                    </div>
                </div>
                <div className="flex gap-2">
                    <button onClick={onNavigateToGsoc} className="flex items-center gap-2 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-md text-sm font-medium transition-colors border border-slate-200">
                        {React.cloneElement(ICONS.gsoc as any, { className: 'h-4 w-4 text-blue-600' })}
                        GSOC Monitor
                    </button>
                    <button onClick={onNavigateToVcmc} className="flex items-center gap-2 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-md text-sm font-medium transition-colors border border-slate-200">
                        {React.cloneElement(ICONS.vcmc as any, { className: 'h-4 w-4 text-purple-600' })}
                        Run Drill
                    </button>
                </div>
            </div>

            <div className="flex-1 grid grid-cols-1 lg:grid-cols-3 gap-6 min-h-0">
                {/* Left — OPORD / Comms / Log */}
                <div className="lg:col-span-2 bg-white rounded-lg border border-slate-200 shadow-sm flex flex-col overflow-hidden">
                    <div className="flex border-b border-slate-200 bg-slate-50">
                        {(['plan', 'comms', 'log'] as const).map(tab => (
                            <button key={tab} onClick={() => setActiveTab(tab)}
                                className={`px-5 py-3 text-sm font-semibold capitalize ${activeTab === tab ? 'bg-white text-blue-600 border-t-2 border-blue-600' : 'text-slate-500 hover:text-slate-700'}`}>
                                {tab === 'plan' ? 'OPORD' : tab === 'comms' ? 'Comms Plan' : 'Mission Log'}
                            </button>
                        ))}
                    </div>

                    <div className="flex-1 overflow-y-auto p-6">
                        {/* OPORD Tab */}
                        {activeTab === 'plan' && (
                            <div className="space-y-6">
                                <div className="prose prose-slate max-w-none">
                                    {project.opordContent ? (
                                        <div dangerouslySetInnerHTML={{ __html: md.render(project.opordContent) }} />
                                    ) : (
                                        <div className="text-center text-slate-500 py-12">
                                            <p>No Operation Order generated yet.</p>
                                            <p className="text-sm mt-1">Return to the Planner to initialise the operation.</p>
                                        </div>
                                    )}
                                </div>
                                {/* Gap 2 & 4 — Persisted Intelligence Annexes */}
                                {(project.piBriefSummary || project.opsRiskSummary) && (
                                    <div className="border-t border-slate-200 pt-4 space-y-3">
                                        <h3 className="text-xs font-bold text-slate-500 uppercase tracking-widest">📎 Intelligence Annexes</h3>
                                        {project.piBriefSummary && (
                                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                                <p className="text-xs font-bold text-blue-700 uppercase tracking-wide mb-1">Annex A — Pre-Movement PI Brief Summary</p>
                                                <p className="text-sm text-slate-700 leading-relaxed">{project.piBriefSummary}</p>
                                            </div>
                                        )}
                                        {project.opsRiskSummary && (
                                            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                                                <p className="text-xs font-bold text-emerald-700 uppercase tracking-wide mb-1">Annex B — Operational Risk Summary</p>
                                                <p className="text-sm text-slate-700 leading-relaxed">{project.opsRiskSummary}</p>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Comms Plan Tab — dynamic, derived from project data */}
                        {activeTab === 'comms' && (
                            <div className="space-y-4">
                                <div className="flex items-center justify-between mb-1">
                                    <h3 className="text-xs font-bold text-slate-500 uppercase tracking-widest">Communications Plan</h3>
                                    <span className="text-[10px] text-slate-400">AO: {commsData.country}</span>
                                </div>

                                {/* Channel assignments */}
                                <div className="bg-slate-50 rounded-lg border border-slate-200 overflow-hidden">
                                    <div className="px-4 py-2 bg-slate-100 border-b border-slate-200">
                                        <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wide">📡 Channel Assignments</h4>
                                    </div>
                                    <div className="divide-y divide-slate-100">
                                        {[
                                            { label: 'Command', value: commsData.command, color: 'text-blue-700' },
                                            { label: 'Tactical', value: commsData.tactical, color: 'text-emerald-700' },
                                            { label: 'Medical / Emergency', value: commsData.medical, color: 'text-red-700' },
                                        ].map(row => (
                                            <div key={row.label} className="flex justify-between items-center px-4 py-2.5 text-sm">
                                                <span className="font-semibold text-slate-600">{row.label}</span>
                                                <span className={`font-mono font-bold ${row.color}`}>{row.value}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Duress & Lost-Comms */}
                                <div className="bg-red-50 rounded-lg border border-red-200 overflow-hidden">
                                    <div className="px-4 py-2 bg-red-100 border-b border-red-200">
                                        <h4 className="text-xs font-bold text-red-800 uppercase tracking-wide">🚨 Security Protocols</h4>
                                    </div>
                                    <div className="px-4 py-3 space-y-2 text-sm">
                                        <div><span className="font-semibold text-red-800">Duress Word: </span><span className="font-mono text-red-700">{commsData.duress}</span></div>
                                        <div><span className="font-semibold text-red-800">Lost-Comms Procedure: </span><span className="text-slate-700">{commsData.lostComms}</span></div>
                                    </div>
                                </div>

                                {/* Emergency numbers — derived from country */}
                                <div className="bg-slate-50 rounded-lg border border-slate-200 overflow-hidden">
                                    <div className="px-4 py-2 bg-slate-100 border-b border-slate-200">
                                        <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wide">🚑 Emergency Numbers — {commsData.country}</h4>
                                    </div>
                                    <div className="grid grid-cols-3 divide-x divide-slate-100 text-center">
                                        {[
                                            { label: 'Police', value: commsData.police, emoji: '👮' },
                                            { label: 'Ambulance', value: commsData.ambulance, emoji: '🚑' },
                                            { label: 'Fire', value: commsData.fire, emoji: '🔥' },
                                        ].map(item => (
                                            <div key={item.label} className="px-4 py-3">
                                                <div className="text-lg">{item.emoji}</div>
                                                <div className="text-xs text-slate-500">{item.label}</div>
                                                <div className="text-lg font-black font-mono text-slate-800">{item.value}</div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <p className="text-[11px] text-slate-400 italic">
                                    Emergency numbers derived from AO country. Configure channel details and duress word via the Advance Site Survey workflow.
                                </p>
                            </div>
                        )}

                        {/* Mission Log Tab */}
                        {activeTab === 'log' && (
                            <div className="flex flex-col h-full gap-4">
                                <div className="flex-1 space-y-2 overflow-y-auto max-h-80">
                                    {logEntries.map((entry, i) => (
                                        <div key={i} className={`rounded-lg px-4 py-2.5 border text-sm ${
                                            entry.type === 'warning' ? 'bg-amber-50 border-amber-200' :
                                            entry.type === 'action' ? 'bg-blue-50 border-blue-200' :
                                            'bg-slate-50 border-slate-200'}`}>
                                            <div className="flex items-center justify-between mb-0.5">
                                                <span className="text-xs font-bold text-slate-600">{entry.author}</span>
                                                <span className="text-[11px] font-mono text-slate-400">{entry.time}</span>
                                            </div>
                                            <p className="text-slate-700">{entry.message}</p>
                                        </div>
                                    ))}
                                </div>
                                {/* Log input */}
                                <div className="border-t border-slate-200 pt-4 space-y-2">
                                    <div className="flex gap-2">
                                        <input
                                            value={logAuthor}
                                            onChange={e => setLogAuthor(e.target.value)}
                                            className="w-36 px-3 py-2 text-xs border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                                            placeholder="Your call-sign"
                                        />
                                        <input
                                            value={newLogEntry}
                                            onChange={e => setNewLogEntry(e.target.value)}
                                            onKeyDown={e => e.key === 'Enter' && handleAddLog()}
                                            className="flex-1 px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                                            placeholder="Log entry… (Enter to submit)"
                                        />
                                        <button onClick={handleAddLog} disabled={isSavingLog}
                                            className="px-4 py-2 bg-blue-600 text-white text-sm font-bold rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-60">
                                            {isSavingLog ? '…' : 'Log'}
                                        </button>
                                    </div>
                                    <p className="text-[11px] text-emerald-600">✓ Entries are persisted to the project record in real-time.</p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Right — Map & Team Status */}
                <div className="flex flex-col gap-6">
                    {/* Live Map — with route markers */}
                    <div className="bg-slate-900 rounded-lg overflow-hidden border border-slate-700 shadow-lg flex-1 min-h-[300px] relative">
                        <div className="absolute top-2 left-2 z-10 bg-slate-900/80 text-white text-xs px-2 py-1 rounded backdrop-blur-sm">
                            LIVE TRACKING // {project.location?.address || 'Unknown Location'}
                        </div>
                        {/* Route legend */}
                        {tacticalRoutes.length > 0 && (
                            <div className="absolute bottom-2 left-2 z-10 bg-slate-900/90 text-white text-[10px] px-2 py-1.5 rounded backdrop-blur-sm space-y-1">
                                <div className="flex items-center gap-1.5"><span className="w-4 h-0.5 bg-blue-500 inline-block" />Primary</div>
                                <div className="flex items-center gap-1.5"><span className="w-4 h-0.5 bg-amber-400 inline-block border-dashed" />Secondary</div>
                                <div className="flex items-center gap-1.5"><span className="w-4 h-0.5 bg-red-500 inline-block" />EER</div>
                            </div>
                        )}
                        <UnifiedMap
                            initialViewState={mapCenter}
                            theme="dark"
                            markers={mapMarkers}
                            tacticalRoutes={tacticalRoutes.length > 0 ? tacticalRoutes : undefined}
                            isLoaded={isLoaded}
                        />
                    </div>

                    {/* Golden Hour widget */}
                    {(project.plannerData?.ep_adv_nearest_hospital || project.plannerData?.ep_adv_hospital_distance) && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                            <h3 className="text-xs font-bold text-red-800 uppercase tracking-wide mb-2">🏥 Golden Hour</h3>
                            <p className="text-sm font-bold text-red-900">{project.plannerData?.ep_adv_nearest_hospital || 'Unknown'}</p>
                            <p className="text-xs text-red-700 mt-0.5">{project.plannerData?.ep_adv_hospital_distance || 'Distance not recorded'}</p>
                        </div>
                    )}

                    {/* Team Status */}
                    <div className="bg-white p-4 rounded-lg border border-slate-200 shadow-sm">
                        <h3 className="font-bold text-slate-700 mb-3 text-sm uppercase tracking-wider">Team Status</h3>
                        <div className="space-y-2">
                            {[
                                { role: 'Detail Leader', status: 'Online', color: 'bg-green-500' },
                                { role: 'Advance', status: 'Online', color: 'bg-green-500' },
                                { role: 'Transport', status: 'Standby', color: 'bg-yellow-500' },
                            ].map(agent => (
                                <div key={agent.role} className="flex justify-between items-center p-2 bg-slate-50 rounded">
                                    <div className="flex items-center gap-2">
                                        <span className={`w-2 h-2 ${agent.color} rounded-full`} />
                                        <span className="text-sm font-medium text-slate-700">{agent.role}</span>
                                    </div>
                                    <span className="text-xs text-slate-500">{agent.status}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EpOperationsDashboard;
